import { Dialog } from "@headlessui/react";
import { InformationCircleIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { useConfigurations } from "@via/api/configuration";
import { Spinner } from "@via/components/loading/loading.tsx";
import SelectComponent from "@via/components/select/select.tsx";
import {
  useGetSavedCredentials,
  useTestSSHConnection,
} from "@via/pages/account/profile/profile.api.tsx";
import {
  ContainerTypeOptions,
  DefaultValues,
  ExecutorJobOptions,
  ExecutorJobsExternals,
  ExecutorJobsLocals,
  ExecutorOptions,
  executorSelectValue,
  ProxyOptions,
} from "@via/pages/account/profile/run-environments/run-environment-form/run-environment-constants.ts";
import {
  ExecutorExternals,
  ExecutorJobsExternal,
  ExecutorJobsLocal,
  ExecutorLocal,
  MaximumExecutorJobLimit,
} from "@via/pages/account/profile/run-environments/run-environment-form/run-environment-form-helpers.tsx";
import { createRunEnvironmentOptions } from "@via/utils/form-validators.tsx";
import { removeEmptyStrings } from "@via/utils/helpers.tsx";
import {
  CredentialView,
  ExecutorJobsType,
  ExecutorType,
  type ProfileClusterForClient,
} from "common";
import { JSX, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { Tooltip } from "react-tooltip";
import {
  useCreateOrganizationRunEnvironment,
  useUpdateOrganizationRunEnvironment,
} from "@via/pages/organization/run-environments/organization-run-environments.api.tsx";
import SharedWithProjects from "@via/pages/account/profile/run-environments/shared-with-projects.tsx";

/** One credential the form may name, as the selectors want it. */
export type RunEnvironmentCredentialOption = { label: string; value: number };

/**
 * Everything the form needs from the world around it.
 *
 * The form used to fetch its own credentials as the signed-in user and write through the
 * organization's own routes, which is why it could only ever be rendered on the organization page.
 * The admin console has to render the same form for an organization the administrator is not a
 * member of, against a different set of routes, so the two surfaces supply this instead. Data, not
 * hooks: hooks cannot be called conditionally, and the caller is the one holding the right ones.
 */
export type RunEnvironmentFormAdapter = {
  credentialOptions: {
    amazon: RunEnvironmentCredentialOption[];
    google: RunEnvironmentCredentialOption[];
    ssh: RunEnvironmentCredentialOption[];
  };
  isCredentialOptionsLoading: boolean;
  /** True when the options could not be loaded at all, which is not the same as none existing. */
  credentialOptionsError?: boolean;
  onRetryCredentialOptions?: () => void;
  /**
   * Omitted in admin context, which is what disables the Test Connection control: the test runs as
   * the credential's owner, and a site administrator is not that person (spec decision 7).
   */
  testSshConnection?: (args: {
    sshId?: number;
    username?: string | null;
    hostname?: string | null;
    port?: string | null;
  }) => void;
  isTestingSshConnection: boolean;
  save: (payload: ProfileClusterForClient) => Promise<void>;
  isSaving: boolean;
};

/** The executor choices that cannot be saved without a credential to run as. */
const EXECUTOR_JOBS_REQUIRING_A_CREDENTIAL: ExecutorJobsType[] = [
  ExecutorJobsType.AwsBatch,
  ExecutorJobsType.GoogleBatch,
];

export function RunEnvironmentFormView({
  setOpen,
  editableRunEnvironment,
  adapter,
}: {
  setOpen: (open: boolean) => void;
  editableRunEnvironment?: ProfileClusterForClient;
  adapter: RunEnvironmentFormAdapter;
}): JSX.Element {
  /**
   * "draft" is the pre-populated state for run environment form
   */
  const [draft, setDraft] = useState<ProfileClusterForClient>();
  const [selectedExecutor, setSelectedExecutor] = useState<ExecutorType>(
    ExecutorType.Local
  );
  const [selectedProxy, setSelectedProxy] = useState("none");
  const [selectedExecutorJobs, setSelectedExecutorJobs] =
    useState<ExecutorJobsType>(ExecutorJobsType.Local);
  const [selectedSshId, setSelectedSshId] = useState<number>();
  const [selectedContainerType, setSelectedContainerType] = useState(
    ContainerTypeOptions[0].value
  );
  const [selectedAmazonId, setSelectedAmazonId] = useState<number>();
  const [selectedGoogleId, setSelectedGoogleId] = useState<number>();

  const isSavedCredentialsLoading = adapter.isCredentialOptionsLoading;
  const credentialsUnavailable = adapter.credentialOptionsError === true;
  const sshCredentials = adapter.credentialOptions.ssh;
  const amazonCredentials = adapter.credentialOptions.amazon;
  const googleCredentials = adapter.credentialOptions.google;

  const {
    register,
    handleSubmit,
    formState: { errors },
    getValues,
    reset,
  } = useForm<ProfileClusterForClient>({
    mode: "onChange",
    shouldUnregister: true,
    defaultValues: editableRunEnvironment ? draft : DefaultValues,
  });

  const { data: configurations } = useConfigurations();

  /**
   * If selectedExecutor is "local", user can pick anything for selectedExecutorJobs.
   * If selectedExecutor is not "local", selectedExecutorJobs is automatically selected and cannot be changed
   */
  const isExecutorJobsSelectorDisabled =
    selectedExecutor !== ExecutorOptions[0].value;

  /**
   * If the form component is called for editing, set the editing data to the draft state
   * and update the selected executor and executor jobs.
   */
  useEffect(() => {
    editableRunEnvironment?.proxy &&
      setSelectedProxy(editableRunEnvironment.proxy);
    editableRunEnvironment?.executor &&
      setSelectedExecutor(editableRunEnvironment.executor);
    editableRunEnvironment?.executorJob &&
      setSelectedExecutorJobs(editableRunEnvironment.executorJob);
    editableRunEnvironment?.sshId &&
      setSelectedSshId(editableRunEnvironment.sshId);
    editableRunEnvironment?.containerType &&
      setSelectedContainerType(editableRunEnvironment.containerType);
    editableRunEnvironment?.amazonCreId &&
      setSelectedAmazonId(editableRunEnvironment.amazonCreId);
    editableRunEnvironment?.googleCreId &&
      setSelectedGoogleId(editableRunEnvironment.googleCreId);

    /**
     * Assign editableRunEnvironment to draft on editing mode.
     * Decode and assign "variable" and "bashVariable" values on editing mode.
     */
    if (editableRunEnvironment) {
      const { bashVariable, variable, hideWorkdir, runCommand, ...rest } =
        editableRunEnvironment;

      const updatedRunEnvironment: ProfileClusterForClient = {
        hideWorkdir,
        ...rest,
      };

      if (bashVariable) {
        updatedRunEnvironment.bashVariable = bashVariable;
      }

      if (variable) {
        updatedRunEnvironment.variable = variable;
      }

      if (runCommand) {
        updatedRunEnvironment.runCommand = runCommand;
      }

      updatedRunEnvironment.hideWorkdir = !!(
        hideWorkdir === "1" || hideWorkdir === "on"
      );

      setDraft(updatedRunEnvironment);
    }
  }, [editableRunEnvironment]);
  /**
   * Reset form state to inject default values
   */
  useEffect(() => {
    reset(draft);
  }, [draft]);

  const handleCreateProfile = async (
    payload: ProfileClusterForClient
  ): Promise<void> => {
    payload.containerType = selectedContainerType;
    payload.executor = selectedExecutor;
    payload.executorJob = selectedExecutorJobs;
    payload.proxy = selectedProxy;
    selectedSshId && (payload.sshId = selectedSshId);
    selectedAmazonId && (payload.amazonCreId = selectedAmazonId);
    selectedGoogleId && (payload.googleCreId = selectedGoogleId);

    /**
     * hideWorkdir is booleans. However, backend expects it in string format (example: "1" or "0")
     */
    payload.hideWorkdir = Number(payload.hideWorkdir ?? false).toString();

    // Which route this reaches, and whether it creates or updates, is the adapter's business.
    await adapter.save(removeEmptyStrings(payload) as ProfileClusterForClient);
  };

  const handleExecutorChange = (executor: any) => {
    setSelectedExecutor(executor.value);

    /**
     * If executor is not local, selectedExecutorJobs needs to be same as selectedExecutor
     */
    if (executor.value !== ExecutorOptions[0].value) {
      setSelectedExecutorJobs(executor.value);
    }
  };

  const handleExecutorJobsChange = (executorJobs: any) => {
    setSelectedExecutorJobs(executorJobs.value);
  };

  const handleProxyChange = (proxy: any) => {
    setSelectedProxy(proxy.value);
  };

  const handleSshIdChange = (sshId: any) => {
    setSelectedSshId(sshId.value);
  };

  const handleAwsIdChange = (amazonId: any) => {
    setSelectedAmazonId(amazonId?.value);
  };

  const handleGoogleIdChange = (googleId: any) => {
    setSelectedGoogleId(googleId?.value);
  };

  const handleContainerTypeChange = (containerType: any) => {
    setSelectedContainerType(containerType.value);
  };

  return (
    <form
      className="flex h-full flex-col divide-y divide-gray-200 bg-white shadow-xl"
      onSubmit={e => {
        e.preventDefault();
        void handleSubmit(handleCreateProfile)(e);
      }}>
      <div className="h-0 flex-1 overflow-y-auto">
        <div className="border-b bg-gray-50 px-4 py-6 sm:px-6">
          <div className="flex items-center justify-between">
            <Dialog.Title className="text-base font-semibold leading-6 text-gray-900">
              {editableRunEnvironment
                ? "Edit Run Environment"
                : "New Run Environment"}
            </Dialog.Title>
            <div className="ml-3 flex h-7 items-center">
              <button
                type="button"
                className="relative rounded-md text-gray-900 duration-150 ease-in-out hover:text-gray-600 focus:outline-none"
                onClick={() => {
                  setOpen(false);
                }}>
                <span className="absolute -inset-2.5" />
                <span className="sr-only">Close panel</span>
                <XMarkIcon className="h-6 w-6" aria-hidden="true" />
              </button>
            </div>
          </div>
          <div className="mt-1">
            <p className="text-sm text-gray-500">
              {editableRunEnvironment
                ? "Edit your run environment details below."
                : "Get started by filling in the information below to create your new run environment."}
            </p>
          </div>
        </div>

        {credentialsUnavailable && (
          <div
            data-testid="credential-options-error"
            className="mx-4 mt-4 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 sm:mx-6">
            <div>
              The saved credentials could not be loaded, so the credential
              selectors are unavailable. Everything else on this form still
              works.
            </div>
            {adapter.onRetryCredentialOptions && (
              <button
                type="button"
                className="mt-2 rounded-md bg-white px-2 py-1 text-xs font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                onClick={adapter.onRetryCredentialOptions}>
                Retry
              </button>
            )}
          </div>
        )}

        <div className="flex flex-1 flex-col justify-between">
          <div className="divide-y divide-gray-200 px-4 sm:px-6">
            <div className="space-y-6 pb-5 pt-6">
              <div>
                <label className="block text-sm font-medium leading-6 text-gray-900">
                  Profile Name <span className="text-red-400">*</span>
                </label>
                <div className="mt-2">
                  <input
                    {...register("name", createRunEnvironmentOptions.name)}
                    type="text"
                    className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                    placeholder="Name your Profile"
                  />
                  {errors?.name ? (
                    <span className="text-xs text-rose-500">
                      {errors.name.message}
                    </span>
                  ) : null}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium leading-6 text-gray-900">
                  User Name
                </label>
                <div className="mt-2">
                  <input
                    {...register("username")}
                    type="text"
                    className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                    placeholder="Name your User"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium leading-6 text-gray-900">
                  Host Name
                </label>
                <div className="mt-2">
                  <input
                    {...register("hostname")}
                    type="text"
                    className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                    placeholder="Name your Host"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium leading-6 text-gray-900">
                  SSH Port (optional)
                </label>
                <div className="mt-2">
                  <input
                    {...register("port")}
                    type="text"
                    className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                    placeholder="Your SSH Port"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium leading-6 text-gray-900">
                  Proxy (optional)
                </label>
                <div className="mt-2" data-testid="proxy">
                  <SelectComponent
                    rules={createRunEnvironmentOptions.proxy}
                    onChange={handleProxyChange}
                    value={ProxyOptions.find(
                      ({ value }) => value === selectedProxy
                    )}
                    name="proxy"
                    options={ProxyOptions}
                    enableClear={false}
                  />
                  {errors.proxy && (
                    <span className="text-xs text-rose-500">
                      {errors.proxy.message}
                    </span>
                  )}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium leading-6 text-gray-900">
                  SSH Keys
                </label>
                <div className="mt-2" data-testid="ssh-id">
                  <SelectComponent
                    name="sshId"
                    onChange={handleSshIdChange}
                    value={sshCredentials.find(
                      ssh => ssh.value === selectedSshId
                    )}
                    options={sshCredentials}
                    isLoading={isSavedCredentialsLoading}
                    readOnly={credentialsUnavailable}
                    enableClear={false}
                    placeholder={"Select SSH Keys"}
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium leading-6 text-gray-900">
                  Nextflow Path
                </label>
                <div className="mt-2">
                  <input
                    {...register("nextPath")}
                    type="text"
                    className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                    placeholder="Your Nextflow Path"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium leading-6 text-gray-900">
                  Singularity Cache Folder
                </label>
                <div className="mt-2">
                  <input
                    {...register("singuCache")}
                    type="text"
                    className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                    placeholder="Your Singularity Cache Folder"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium leading-6 text-gray-900">
                  Profile Variables
                </label>
                <div className="mt-2">
                  <textarea
                    {...register("variable")}
                    className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                    placeholder="Your Profile Variables"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium leading-6 text-gray-900">
                  Environment Variables
                </label>
                <div className="mt-2">
                  <textarea
                    {...register("bashVariable")}
                    className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                    placeholder="Your Environment Variables"
                  />
                </div>
              </div>
              {configurations?.SHOW_RUN_COMMAND && (
                <div>
                  <label className="block text-sm font-medium leading-6 text-gray-900">
                    Run Command
                  </label>
                  <div className="mt-2">
                    <textarea
                      {...register("runCommand")}
                      className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                      placeholder="Enter Run Commands"
                    />
                  </div>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium leading-6 text-gray-900">
                  Container Type
                </label>
                <div className="mt-2" data-testid="container-type">
                  <SelectComponent
                    name="containerType"
                    onChange={handleContainerTypeChange}
                    value={ContainerTypeOptions.find(
                      ({ value }) => value === selectedContainerType
                    )}
                    options={ContainerTypeOptions}
                    enableClear={false}
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium leading-6 text-gray-900">
                  Default Container Options (optional)
                </label>
                <div className="mt-2" data-testid="container-options">
                  <input
                    {...register("containerOptions")}
                    type="text"
                    className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                    placeholder={`e.g. --bind /data:/data`}
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium leading-6 text-gray-900">
                  Executor of Nextflow
                </label>
                <div className="my-2" data-testid="executor">
                  <SelectComponent
                    name="executor"
                    onChange={handleExecutorChange}
                    value={executorSelectValue(selectedExecutor)}
                    options={ExecutorOptions}
                    enableClear={false}
                  />
                </div>

                <div className="mt-4 p-4 shadow-sm ring-1 ring-gray-900/5 sm:rounded-lg">
                  <label className="block text-sm font-medium leading-6 text-gray-900">
                    Executor Settings for Nextflow
                  </label>
                  {selectedExecutor === ExecutorType.Local ? (
                    <ExecutorLocal register={register} />
                  ) : (
                    <ExecutorExternals
                      register={register}
                      selectedExecutor={selectedExecutor}
                    />
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium leading-6 text-gray-900">
                  Executor of Nextflow Jobs
                </label>
                <div className="my-2" data-testid="executor-job">
                  <SelectComponent
                    name="executorJob"
                    onChange={handleExecutorJobsChange}
                    readOnly={isExecutorJobsSelectorDisabled}
                    value={ExecutorJobOptions.find(
                      ({ value }) => value === selectedExecutorJobs
                    )}
                    options={ExecutorJobOptions}
                    enableClear={false}
                  />
                </div>

                <div className="mt-4 p-4 shadow-sm ring-1 ring-gray-900/5 sm:rounded-lg">
                  {ExecutorJobsLocals.includes(selectedExecutorJobs) ? (
                    <ExecutorJobsLocal
                      register={register}
                      selectedExecutorJobs={selectedExecutorJobs}
                    />
                  ) : ExecutorJobsExternals.includes(selectedExecutorJobs) ? (
                    <ExecutorJobsExternal
                      register={register}
                      selectedExecutorJobs={selectedExecutorJobs}
                    />
                  ) : null}
                </div>
              </div>

              <div>
                <div className="mt-4 p-4 shadow-sm ring-1 ring-gray-900/5 sm:rounded-lg">
                  <MaximumExecutorJobLimit register={register} />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium leading-6 text-gray-900">
                  Default Work Directory (Optional)
                </label>
                <div className="mt-2">
                  <input
                    {...register("defNextflowWorkdir")}
                    type="text"
                    className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                    placeholder="Your Default Work Directory"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium leading-6 text-gray-900">
                  Default Execution Directory
                </label>
                <div className="mt-2">
                  <input
                    {...register("defWorkdir")}
                    type="text"
                    className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                    placeholder="Your Default Execution Directory"
                  />
                </div>
              </div>

              {selectedExecutorJobs === ExecutorJobsType.AwsBatch && (
                <div>
                  <label className="block text-sm font-medium leading-6 text-gray-900">
                    AWS Credentials
                  </label>
                  <div className="mt-2" data-testid="amazon-id">
                    <SelectComponent
                      name="amazonId"
                      onChange={handleAwsIdChange}
                      value={amazonCredentials.find(
                        aws => aws.value === selectedAmazonId
                      )}
                      options={amazonCredentials}
                      isLoading={isSavedCredentialsLoading}
                      readOnly={credentialsUnavailable}
                      enableClear={true}
                      placeholder={"Select Amazon Credentials"}
                    />
                  </div>
                </div>
              )}

              {selectedExecutorJobs === ExecutorJobsType.GoogleBatch && (
                <div>
                  <div>
                    <label className="block text-sm font-medium leading-6 text-gray-900">
                      Google Cloud Credentials
                    </label>
                    <div className="mt-2" data-testid="google-id">
                      <SelectComponent
                        name="googleId"
                        onChange={handleGoogleIdChange}
                        value={googleCredentials.find(
                          google => google.value === selectedGoogleId
                        )}
                        options={googleCredentials}
                        isLoading={isSavedCredentialsLoading}
                        readOnly={credentialsUnavailable}
                        enableClear={true}
                        placeholder={"Select Google Cloud Credentials"}
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block mt-2 flex items-center gap-2 text-sm font-medium leading-6 text-gray-900">
                      Region
                      <InformationCircleIcon
                        id="tooltip-region"
                        className="h-5 w-5"
                      />
                    </label>
                    <Tooltip
                      anchorSelect="#tooltip-region"
                      content="Make sure to specify a location where Google Batch is available. Refer to the Google Batch documentation for region availability."
                    />
                    <div className="mt-2">
                      <input
                        {...register("region")}
                        type="text"
                        className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                        placeholder="Enter Region"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block mt-2 flex items-center gap-2 text-sm font-medium leading-6 text-gray-900">
                      Project Id
                      <InformationCircleIcon
                        id="tooltip-project-id"
                        className="h-5 w-5"
                      />
                    </label>
                    <Tooltip
                      className={"max-w-96"}
                      anchorSelect="#tooltip-project-id"
                      content={`If you already selected Google Key, you can leave this section empty. Otherwise, please get Project ID by following these steps: Open the Google Cloud Console. On the dashboard, find 'Project Info' box and copy 'Project ID' field. eg.Foundry-193616"`}
                    />
                    <div className="mt-2">
                      <input
                        {...register("projectId")}
                        type="text"
                        className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                        placeholder="Enter Project Id"
                      />
                    </div>
                  </div>
                  <div className="flex items-center gap-2 mt-2">
                    <label className="block text-sm font-medium leading-6 text-gray-900">
                      Spot Instance
                    </label>
                    <div>
                      <input
                        {...register("spotInstance")}
                        type="checkbox"
                        className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-600"
                      />
                    </div>
                  </div>
                </div>
              )}
              <div>
                <label className="block text-sm font-medium leading-6 text-gray-900">
                  Default Publish Directory
                </label>
                <div className="mt-2">
                  <input
                    {...register("defPublishdir")}
                    type="text"
                    className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                    placeholder="Your Default Publish Directory"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium leading-6 text-gray-900">
                  Default Download Directory
                </label>
                <div className="mt-2">
                  <input
                    {...register("defDowndir")}
                    type="text"
                    className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                    placeholder="Base path pipelines use to download reference data (params.DOWNDIR)"
                  />
                </div>
              </div>

              <div className="flex items-center gap-2">
                <label className="block text-sm font-medium leading-6 text-gray-900">
                  Hide Execution Directory
                </label>
                <div>
                  <input
                    {...register("hideWorkdir")}
                    type="checkbox"
                    className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-600"
                  />
                </div>
              </div>
              {editableRunEnvironment?.id ? (
                <SharedWithProjects
                  profileClusterId={editableRunEnvironment.id}
                />
              ) : null}
            </div>
          </div>
        </div>
      </div>

      <div className="flex flex-shrink-0 justify-between px-4 py-4">
        <button
          type="button"
          className="rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
          title={
            adapter.testSshConnection
              ? undefined
              : "Testing a connection runs as the credential's owner, so it is available on the organization's own Run Environments page."
          }
          disabled={
            !adapter.testSshConnection ||
            adapter.isTestingSshConnection ||
            Number.isNaN(Number(selectedSshId)) ||
            !getValues("username") ||
            !getValues("hostname")
          }
          onClick={() => {
            const port = getValues("port");
            adapter.testSshConnection?.({
              sshId: selectedSshId,
              username: getValues("username"),
              hostname: getValues("hostname"),
              ...(port && { port }), // Add port only if it's not empty
            });
          }}>
          <Spinner loading={adapter.isTestingSshConnection}>
            Test Connection
          </Spinner>
        </button>
        <div className="flex flex-shrink-0 justify-end">
          <button
            id="run-environment-cancel"
            type="button"
            className="rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
            onClick={() => {
              setOpen(false);
            }}>
            Cancel
          </button>
          <button
            id="run-environment-save"
            type="submit"
            disabled={
              adapter.isSaving ||
              (credentialsUnavailable &&
                EXECUTOR_JOBS_REQUIRING_A_CREDENTIAL.includes(
                  selectedExecutorJobs
                ))
            }
            className="ml-4 inline-flex justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:cursor-not-allowed disabled:opacity-50">
            <Spinner loading={adapter.isSaving}>Save</Spinner>
          </button>
        </div>
      </div>
    </form>
  );
}

/**
 * The organization page's form: the signed-in user's own credentials, the organization's own
 * routes, and a live connection test.
 *
 * Kept as the default export with the props it has always had, so the two call sites on the
 * organization page did not have to learn about adapters. The admin console builds its own adapter
 * around the same view.
 */
export default function OrganizationRunEnvironmentForm({
  setOpen,
  organizationId,
  editableRunEnvironment,
}: {
  setOpen: (open: boolean) => void;
  organizationId: string;
  editableRunEnvironment?: ProfileClusterForClient;
}): JSX.Element {
  const {
    data: savedCredentials,
    isLoading: isSavedCredentialsLoading,
    isError: isSavedCredentialsError,
    refetch: refetchSavedCredentials,
  } = useGetSavedCredentials({ type: CredentialView.UserOwned });
  const { mutate: testSSHConnection, isPending: isTestSSHConnectionLoading } =
    useTestSSHConnection();

  const {
    mutateAsync: createRunEnvironment,
    isSuccess: isCreateRunEnvironmentSuccess,
    isPending: isCreateRunEnvironmentLoading,
  } = useCreateOrganizationRunEnvironment();
  const {
    mutateAsync: updateRunEnvironment,
    isSuccess: isUpdateRunEnvironmentSuccess,
    isPending: isUpdateRunEnvironmentLoading,
  } = useUpdateOrganizationRunEnvironment();

  useEffect(() => {
    if (isCreateRunEnvironmentSuccess || isUpdateRunEnvironmentSuccess) {
      setOpen(false);
    }
  }, [isCreateRunEnvironmentSuccess, isUpdateRunEnvironmentSuccess]);

  const toOptions = (
    saved: { id: number; name?: string }[] | undefined
  ): RunEnvironmentCredentialOption[] =>
    (saved || []).map(({ id, name }) => ({ label: name ?? "", value: id }));

  const adapter: RunEnvironmentFormAdapter = {
    credentialOptions: {
      amazon: toOptions(savedCredentials?.amazonCredentials),
      google: toOptions(savedCredentials?.googleCredentials),
      ssh: toOptions(savedCredentials?.ssh),
    },
    isCredentialOptionsLoading: isSavedCredentialsLoading,
    credentialOptionsError: isSavedCredentialsError,
    onRetryCredentialOptions: () => {
      void refetchSavedCredentials();
    },
    testSshConnection: testSSHConnection,
    isTestingSshConnection: isTestSSHConnectionLoading,
    save: async payload => {
      if (editableRunEnvironment?.id) {
        await updateRunEnvironment({
          requestParams: {
            id: organizationId,
            runEnvironmentId: editableRunEnvironment.id,
          },
          requestBody: payload,
        });
      } else {
        await createRunEnvironment({
          requestParams: { id: organizationId },
          requestBody: payload,
        });
      }
    },
    isSaving: isCreateRunEnvironmentLoading || isUpdateRunEnvironmentLoading,
  };

  return (
    <RunEnvironmentFormView
      setOpen={setOpen}
      editableRunEnvironment={editableRunEnvironment}
      adapter={adapter}
    />
  );
}
